// src/components/CookiesPolicy.jsx
import React, { useState, useEffect } from 'react';
import './LegalPages.css';

function CookiesPolicy() {
    const [cookieConsent, setCookieConsent] = useState(null);

    useEffect(() => {
        // Check if user has already consented
        const savedConsent = localStorage.getItem('cookieConsent');
        if (savedConsent) {
            setCookieConsent(JSON.parse(savedConsent));
        }
    }, []);

    const acceptCookies = () => {
        const consent = {
            accepted: true,
            timestamp: new Date().toISOString(),
            preferences: {
                essential: true,
                analytics: true,
                functional: true
            }
        };
        localStorage.setItem('cookieConsent', JSON.stringify(consent));
        setCookieConsent(consent);
    };

    const rejectCookies = () => {
        const consent = {
            accepted: false,
            timestamp: new Date().toISOString(),
            preferences: {
                essential: true, // Essential cookies always on
                analytics: false,
                functional: false
            }
        };
        localStorage.setItem('cookieConsent', JSON.stringify(consent));
        setCookieConsent(consent);
    };

    return (
        <div className="legal-page">
            <div className="legal-container">
                <h1>🍪 Cookies Policy</h1>
                <p className="last-updated">Last Updated: April 3, 2025</p>

                <section>
                    <h2>What Are Cookies?</h2>
                    <p>Cookies are small text files stored on your device when you visit websites. They help websites remember information about your visit and preferences.</p>
                </section>

                <section>
                    <h2>How Cookies Are Automatically Set</h2>
                    <p><strong>You don't need to do anything!</strong> Cookies are automatically set by:</p>
                    <ul>
                        <li><strong>Your Browser:</strong> Chrome, Firefox, Safari, etc. automatically handle cookies</li>
                        <li><strong>Web Servers:</strong> Vercel/Render send cookies with responses</li>
                        <li><strong>JavaScript:</strong> Our code can read/write cookies (only with your consent)</li>
                    </ul>
                </section>

                <section>
                    <h2>Cookies We Use</h2>
                    
                    <h3>Essential Cookies (Always Active)</h3>
                    <p>These cookies are necessary for the website to function properly:</p>
                    <ul>
                        <li><strong>Session Cookies:</strong> Maintain your browsing session</li>
                        <li><strong>Security Cookies:</strong> Help protect against CSRF attacks</li>
                        <li><strong>Load Balancer Cookies:</strong> Ensure consistent server response</li>
                    </ul>

                    <h3>Functional Cookies (With Consent)</h3>
                    <ul>
                        <li><strong>Preferences:</strong> Remember your filter selections</li>
                        <li><strong>UI Settings:</strong> Remember sidebar open/close state</li>
                        <li><strong>Language:</strong> Preferred language settings</li>
                    </ul>

                    <h3>Analytics Cookies (With Consent)</h3>
                    <ul>
                        <li><strong>Page Views:</strong> Which tools and categories you visit</li>
                        <li><strong>Time Spent:</strong> How long you browse</li>
                        <li><strong>Click Tracking:</strong> Which buttons you click</li>
                        <li><strong>Scroll Depth:</strong> How far you scroll on pages</li>
                    </ul>
                </section>

                <section>
                    <h2>How to Manage Cookies</h2>
                    <p>You can control cookies through your browser settings:</p>
                    
                    <h3>Chrome/Edge/Brave:</h3>
                    <ul>
                        <li>Settings → Privacy and Security → Cookies and other site data</li>
                        <li>Choose: Block all cookies, Block third-party cookies, or Allow all</li>
                    </ul>

                    <h3>Firefox:</h3>
                    <ul>
                        <li>Options → Privacy & Security → Cookies and Site Data</li>
                        <li>Choose: Standard, Strict, or Custom</li>
                    </ul>

                    <h3>Safari:</h3>
                    <ul>
                        <li>Preferences → Privacy → Cookies and website data</li>
                        <li>Choose: Always block, Allow from current websites only, or Always allow</li>
                    </ul>
                </section>

                <section>
                    <h2>Cookie Consent Management</h2>
                    <p>We respect your privacy. You can choose your cookie preferences:</p>
                    
                    {!cookieConsent && (
                        <div className="cookie-consent-demo">
                            <h3>Cookie Preferences</h3>
                            <div className="cookie-options">
                                <label>
                                    <input type="checkbox" checked disabled /> 
                                    Essential Cookies (Required)
                                </label>
                                <label>
                                    <input type="checkbox" /> 
                                    Functional Cookies
                                </label>
                                <label>
                                    <input type="checkbox" /> 
                                    Analytics Cookies
                                </label>
                            </div>
                            <div className="cookie-buttons">
                                <button onClick={acceptCookies} className="btn-accept">
                                    Accept All
                                </button>
                                <button onClick={rejectCookies} className="btn-reject">
                                    Reject Non-Essential
                                </button>
                            </div>
                        </div>
                    )}

                    {cookieConsent && (
                        <div className="cookie-status">
                            <p>✅ You have {cookieConsent.accepted ? 'accepted' : 'rejected'} non-essential cookies on {new Date(cookieConsent.timestamp).toLocaleDateString()}</p>
                            <button onClick={() => {
                                localStorage.removeItem('cookieConsent');
                                setCookieConsent(null);
                            }} className="btn-reset">
                                Reset Cookie Preferences
                            </button>
                        </div>
                    )}
                </section>

                <section>
                    <h2>Third-Party Cookies</h2>
                    <p>We currently do not use third-party cookies. If we add features like analytics or advertising in the future, we will update this policy.</p>
                </section>

                <section>
                    <h2>How to Clear Existing Cookies</h2>
                    <p>To clear cookies already stored on your device:</p>
                    <ol>
                        <li>Open your browser settings</li>
                        <li>Find "Privacy" or "Clear browsing data"</li>
                        <li>Select "Cookies and other site data"</li>
                        <li>Choose time range (Last hour, Last 24 hours, All time)</li>
                        <li>Click "Clear data"</li>
                    </ol>
                </section>

                <section>
                    <h2>Changes to This Policy</h2>
                    <p>We may update this Cookies Policy. Check the "Last Updated" date at the top for changes.</p>
                </section>

                <section>
                    <h2>Questions About Cookies?</h2>
                    <p>Contact us at: <strong>c84289798@gmail.com</strong></p>
                </section>
            </div>
        </div>
    );
}

export default CookiesPolicy;